import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/contexts/TradeAuthContext";
import axios from "axios";
import { 
  LayoutDashboard, Ship, Users, Calculator, FileText, 
  TrendingUp, LogOut, Sparkles, ArrowRight, Bot, Container, Menu, X, UserCog, Handshake, StickyNote, Receipt, Zap, UserPlus, MessageCircle, MapPin, Mic, Megaphone, BookOpen, Bell
} from "lucide-react";
import { toast } from "sonner";

const TradeNav = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, API, authHeader } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dueReminders, setDueReminders] = useState([]);
  const [remindersOpen, setRemindersOpen] = useState(false);

  useEffect(() => {
    const fetchDueReminders = () => {
      axios.get(`${API}/trade/notes/reminders/due`, { headers: authHeader() })
        .then(res => setDueReminders(res.data))
        .catch(() => {});
    };
    fetchDueReminders();
    window.addEventListener("trade-reminders-changed", fetchDueReminders);
    return () => window.removeEventListener("trade-reminders-changed", fetchDueReminders);
  }, [location.pathname]);

  const markReminderDone = async (noteId) => {
    try {
      await axios.patch(`${API}/trade/notes/${noteId}/reminder`, { done: true }, { headers: authHeader() });
      setDueReminders(prev => prev.filter(n => n.id !== noteId));
      window.dispatchEvent(new Event("trade-reminders-changed"));
      toast.success("Reminder marked done");
    } catch {
      toast.error("Could not update reminder");
    }
  };

  const links = [
    { name: "Dashboard", path: "/trade", icon: LayoutDashboard },
    { name: "Shipments", path: "/trade/shipments", icon: Ship },
    { name: "Tracking", path: "/trade/tracking", icon: Container },
    { name: "Suppliers", path: "/trade/suppliers", icon: Users },
    { name: "Agents", path: "/trade/agents", icon: UserCog },
    { name: "Brokers", path: "/trade/brokers", icon: Handshake },
    { name: "Advertise", path: "/trade/advertise", icon: Megaphone },
    { name: "Education", path: "/trade/education", icon: BookOpen },
    { name: "Customers", path: "/trade/customers", icon: UserPlus },
    { name: "WeChat", path: "/trade/wechat", icon: MessageCircle },
    { name: "Addresses", path: "/trade/addresses", icon: MapPin },
    { name: "Audio", path: "/trade/audio", icon: Mic },
    { name: "Accountant", path: "/trade/accountant", icon: Receipt },
    { name: "Gov & People", path: "/trade/gov-helpers", icon: Zap },
    { name: "AI Assistant", path: "/trade/ai", icon: Bot },
    { name: "Calculator", path: "/trade/calculator", icon: Calculator },
    { name: "Converter", path: "/trade/converter", icon: TrendingUp },
    { name: "Documents", path: "/trade/documents", icon: FileText },
    { name: "Notes", path: "/trade/notes", icon: StickyNote },
  ];

  const handleLogout = () => {
    logout();
    toast.success("Logged out");
    navigate("/trade/login");
  };

  const isActive = (path) => {
    if (path === "/trade") return location.pathname === "/trade";
    return location.pathname.startsWith(path);
  };

  return (
    <>
      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-20 left-0 right-0 z-30 trade-navy border-b border-white/5 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 trade-btn-primary flex items-center justify-center rounded-sm">
            <Ship className="w-4 h-4" />
          </div>
          <span className="font-serif font-bold text-sm">Trade Admin</span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setRemindersOpen(!remindersOpen)}
            className="relative p-2 trade-gold"
            data-testid="reminder-bell-btn"
          >
            <Bell size={20} />
            {dueReminders.length > 0 && (
              <span className="absolute -top-1 -right-1 min-w-5 h-5 px-1 rounded-full bg-[#D4AF37] text-black text-[10px] font-bold flex items-center justify-center" data-testid="reminder-count">
                {dueReminders.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="p-2 trade-gold"
            data-testid="trade-mobile-menu-btn"
          >
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* Due reminders */}
      <AnimatePresence>
        {remindersOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="fixed right-4 top-[140px] lg:left-64 lg:right-auto lg:top-24 z-40 w-[calc(100%-2rem)] max-w-sm trade-card rounded-sm p-4 shadow-2xl"
            data-testid="reminder-dropdown"
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-serif font-semibold">Due Reminders</h3>
              <button onClick={() => setRemindersOpen(false)} className="p-1 text-white/40 hover:text-white" data-testid="reminder-dropdown-close">
                <X size={16} />
              </button>
            </div>
            {dueReminders.length === 0 ? (
              <p className="text-sm text-white/45 py-4">No due reminders</p>
            ) : (
              <div className="space-y-3 max-h-80 overflow-y-auto">
                {dueReminders.map(note => (
                  <div key={note.id} className="p-3 bg-white/5 rounded-sm" data-testid={`reminder-item-${note.id}`}>
                    <p className="text-sm text-white/80 whitespace-pre-wrap mb-2">{note.text || note.audio_name || "Voice note"}</p>
                    <p className="text-[11px] trade-gold mb-3">{new Date(note.reminder_at).toLocaleString()}</p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => { setRemindersOpen(false); navigate("/trade/notes"); }}
                        className="flex-1 text-xs bg-white/10 hover:bg-white/20 text-white px-3 py-2 rounded-sm"
                        data-testid={`open-reminder-${note.id}`}
                      >
                        Open Note
                      </button>
                      <button
                        onClick={() => markReminderDone(note.id)}
                        className="flex-1 text-xs bg-[#10b981]/10 trade-green border border-[#10b981]/20 px-3 py-2 rounded-sm"
                        data-testid={`reminder-done-${note.id}`}
                      >
                        Done
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile drawer */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ x: "-100%" }}
            animate={{ x: 0 }}
            exit={{ x: "-100%" }}
            transition={{ type: "tween", duration: 0.25 }}
            className="lg:hidden fixed top-[132px] left-0 bottom-0 w-64 trade-navy border-r border-white/5 z-30 overflow-y-auto"
            data-testid="trade-mobile-nav"
          >
            <div className="p-4">
              <nav className="space-y-1">
                {links.map((link) => {
                  const Icon = link.icon;
                  const active = isActive(link.path);
                  return (
                    <Link
                      key={link.path}
                      to={link.path}
                      onClick={() => setMobileOpen(false)}
                      className={`flex items-center gap-3 px-4 py-3 rounded-sm text-sm transition-all ${
                        active 
                          ? "bg-[#D4AF37]/10 text-[#D4AF37] border-l-2 border-[#D4AF37]"
                          : "text-white/60 hover:text-white hover:bg-white/5 border-l-2 border-transparent"
                      }`}
                    >
                      <Icon size={16} strokeWidth={1.5} />
                      <span>{link.name}</span>
                    </Link>
                  );
                })}
              </nav>
              <div className="mt-6 pt-4 border-t border-white/5">
                <button
                  onClick={() => { setMobileOpen(false); handleLogout(); }}
                  className="flex items-center gap-3 w-full px-4 py-3 rounded-sm text-sm text-white/60 hover:text-red-400"
                >
                  <LogOut size={16} strokeWidth={1.5} />
                  <span>Sign Out</span>
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Desktop sidebar */}
      <aside className="fixed left-0 top-20 bottom-0 w-64 trade-navy border-r border-white/5 overflow-y-auto z-30 hidden lg:block">
      <div className="p-6">
        <div className="flex items-center justify-between gap-3 mb-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 trade-btn-primary flex items-center justify-center rounded-sm">
              <Ship className="w-5 h-5" strokeWidth={2} />
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-white/50">Private</p>
              <p className="font-serif font-bold text-sm">Trade Admin</p>
            </div>
          </div>
          <button
            onClick={() => setRemindersOpen(!remindersOpen)}
            className="relative p-2 trade-gold hover:bg-white/5 rounded-sm"
            data-testid="desktop-reminder-bell-btn"
          >
            <Bell size={18} />
            {dueReminders.length > 0 && (
              <span className="absolute -top-1 -right-1 min-w-5 h-5 px-1 rounded-full bg-[#D4AF37] text-black text-[10px] font-bold flex items-center justify-center" data-testid="desktop-reminder-count">
                {dueReminders.length}
              </span>
            )}
          </button>
        </div>

        <nav className="space-y-1">
          {links.map((link, i) => {
            const Icon = link.icon;
            const active = isActive(link.path);
            return (
              <motion.div
                key={link.path}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05 }}
              >
                <Link
                  to={link.path}
                  className={`flex items-center gap-3 px-4 py-3 rounded-sm text-sm transition-all duration-300 ${
                    active 
                      ? "bg-[#D4AF37]/10 text-[#D4AF37] border-l-2 border-[#D4AF37]"
                      : "text-white/60 hover:text-white hover:bg-white/5 border-l-2 border-transparent"
                  }`}
                  data-testid={`trade-nav-${link.name.toLowerCase()}`}
                >
                  <Icon size={16} strokeWidth={1.5} />
                  <span>{link.name}</span>
                  {active && <ArrowRight size={12} className="ml-auto" />}
                </Link>
              </motion.div>
            );
          })}
        </nav>

        <div className="mt-10 pt-6 border-t border-white/5">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 w-full px-4 py-3 rounded-sm text-sm text-white/60 hover:text-red-400 hover:bg-red-500/5 transition-all"
            data-testid="trade-logout-btn"
          >
            <LogOut size={16} strokeWidth={1.5} />
            <span>Sign Out</span>
          </button>
        </div>

        {/* Decorative bottom card */}
        <div className="mt-8 p-4 bg-gradient-to-br from-[#10b981]/10 to-[#D4AF37]/10 border border-white/5 rounded-sm">
          <Sparkles className="w-5 h-5 trade-gold mb-2" />
          <p className="text-xs text-white/60 leading-relaxed">
            Managing imports from <span className="trade-gold">China</span> → <span className="trade-gold">Djibouti</span> → <span className="trade-green">Ethiopia</span>
          </p>
        </div>
      </div>
    </aside>
    </>
  );
};

export default TradeNav;
